import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, Download, ArrowUpRight, ShoppingBag } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import {
  OrderStatusPill,
  ORDER_STATUS_LABEL,
  PAYMENT_LABEL,
} from '@/components/shared/StatusPill';
import { useUrlState } from '@/hooks/useUrlState';
import { adminApi } from '@/services/api';
import { formatNPRCompact } from '@/utils/format';
import { Button, Card, CardContent, Input } from '@nepal-ecom/ui';

interface AdminOrder {
  id: string;
  orderNumber: string;
  status: string;
  paymentMethod: string;
  total: number;
  itemCount: number;
  vendorCount: number;
  vendorNames: string[];
  customerName: string;
  customerEmail: string | null;
  shippingProvince: string | null;
  createdAt: string;
}

export function OrderList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState({
    search: '',
    status: 'all',
    page: '1',
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'orders', filters],
    queryFn: async () => {
      const res = await adminApi.getOrders({
        search: filters.search || undefined,
        status: filters.status === 'all' ? undefined : filters.status,
        page: filters.page,
        limit: 25,
      });
      return res.data.data as {
        data: AdminOrder[];
        total: number;
        page: number;
        pageCount: number;
      };
    },
    placeholderData: (prev) => prev,
  });

  const exportCsv = () => {
    if (!data?.data?.length) return;
    const rows = [
      ['Order #', 'Customer', 'Vendors', 'Items', 'Total NPR', 'Payment', 'Status', 'Province', 'Date'],
      ...data.data.map((o) => [
        o.orderNumber,
        o.customerName,
        o.vendorNames.join(' / '),
        String(o.itemCount),
        String(o.total),
        PAYMENT_LABEL[o.paymentMethod] ?? o.paymentMethod,
        ORDER_STATUS_LABEL[o.status] ?? o.status,
        o.shippingProvince ?? '',
        new Date(o.createdAt).toISOString().slice(0, 10),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Orders"
        description={
          isLoading
            ? 'Loading orders…'
            : `${data?.total ?? 0} orders across all stores`
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={!data?.data?.length}
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        }
      />

      <Card className="mb-5">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value, page: '1' })}
              placeholder="Search by order #…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ status: e.target.value, page: '1' })}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
            >
              <option value="all">All status</option>
              {Object.entries(ORDER_STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <TableSkeleton rows={8} cols={8} />
      ) : isError ? (
        <Card>
          <CardContent className="p-10 text-center">
            <p className="text-sm text-red-600 mb-4">Failed to load orders.</p>
            <Button size="sm" onClick={() => refetch()}>Retry</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Order', 'Customer', 'Vendor', 'Items', 'Total', 'Payment', 'Status', 'Date', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.data?.length === 0 && (
                  <tr><td colSpan={9}><EmptyState icon={ShoppingBag} title="No orders match" description="Adjust your search or filters." /></td></tr>
                )}
                {data?.data?.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => navigate(`/orders/${o.id}`)}
                    className="hover:bg-primary-50/40 cursor-pointer transition-colors group"
                  >
                    <td className="px-4 py-3.5"><p className="text-sm font-semibold text-slate-900 tabular-nums group-hover:text-primary-700">#{o.orderNumber}</p></td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm text-slate-700">{o.customerName}</p>
                      {o.customerEmail && <p className="text-xs text-slate-400">{o.customerEmail}</p>}
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm text-slate-700 truncate max-w-xs">
                        {o.vendorNames[0] ?? '—'}
                        {o.vendorCount > 1 && <span className="text-xs text-slate-400"> +{o.vendorCount - 1}</span>}
                      </p>
                      {o.shippingProvince && <p className="text-xs text-slate-400">{o.shippingProvince}</p>}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-700 tabular-nums">{o.itemCount}</td>
                    <td className="px-4 py-3.5 text-sm font-bold text-slate-900 tabular-nums">{formatNPRCompact(o.total)}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-700 font-medium">{PAYMENT_LABEL[o.paymentMethod] ?? o.paymentMethod}</td>
                    <td className="px-4 py-3.5"><OrderStatusPill status={o.status} /></td>
                    <td className="px-4 py-3.5 text-xs text-slate-500 whitespace-nowrap">
                      {new Date(o.createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}
                    </td>
                    <td className="px-4 py-3.5">
                      <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-primary-600 transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data && data.pageCount > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
              <span>Page {data.page} of {data.pageCount}</span>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline"
                  disabled={data.page <= 1}
                  onClick={() => setFilters({ page: String(data.page - 1) })}
                >Previous</Button>
                <Button
                  size="sm" variant="outline"
                  disabled={data.page >= data.pageCount}
                  onClick={() => setFilters({ page: String(data.page + 1) })}
                >Next</Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
